import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import ReactSelect from '@components/Select';
import AgGrid from '@components/AgGrid';
import {
  buildKotDailySummary,
  getKotLogsForDateRange,
  sortKotLogsByTableThenDate,
} from '@utilities/kotLogging';
import type { KotLogEntry } from '@src/types/kotLogging';
import { kotLoggingColDefs, toKotLogGridRow, type KotLogGridRow } from './colDefs';

import 'react-datepicker/dist/react-datepicker.css';
import '../Report/Report.scss';

const ALL_TABLES = { label: 'All Tables', value: '' };
const ALL_GROUPS = { label: 'All Groups', value: '' };

function SummaryChip({ label, value }: { label: string; value: string | number }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 120,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Paper>
  );
}

type KotExportRow = {
  Table: string;
  'Date / Time': string;
  'Group ID': string;
  Type: string;
  Service: string;
  Qty: number;
  'Items Cost': number;
  Lines: number;
  'Printed by': string;
  Channel: string;
  Source: string;
  Items: string;
  UO: string;
  'Order ID': string;
};

const buildKotExportRows = (rows: KotLogGridRow[]): KotExportRow[] =>
  rows.map((row) => ({
    Table: row.tableNumber || '',
    'Date / Time': row.printedAtMs
      ? moment(row.printedAtMs).local().format('DD MMM YYYY, h:mm:ss a')
      : '',
    'Group ID': row.kotSessionId || '',
    Type: row.printType || '',
    Service: row.serviceLabel || '',
    Qty: Number(row.totalQty) || 0,
    'Items Cost': Number(row.totalItemsCost) || 0,
    Lines: Number(row.itemCount) || 0,
    'Printed by': row.printedByLabel || '',
    Channel: row.printerChannel || '',
    Source: row.source || '',
    Items: row.itemsSummary || '',
    UO: row.uoId || '',
    'Order ID': row.orderId || '',
  }));

const getKotExportFilename = (
  restaurantName: string,
  startDate: Date,
  endDate: Date
): string => {
  const rest = (restaurantName || 'restaurant').replace(/[^\w-]+/g, '_').slice(0, 40);
  return `[KOT]${rest}_${moment(startDate).format('D-MMM-YYYY')}_${moment(endDate).format('D-MMM-YYYY')}`;
};

const downloadExcelFromRows = (rows: KotExportRow[], filename: string) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]) as Array<keyof KotExportRow>;
  const escapeCell = (value: string | number) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const headerHtml = headers.map((h) => `<th>${escapeCell(h)}</th>`).join('');
  const bodyHtml = rows
    .map(
      (row) =>
        `<tr>${headers.map((h) => `<td>${escapeCell(row[h])}</td>`).join('')}</tr>`
    )
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8" /></head><body><table border="1"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const KotLoggingReport = ({
  isSuperAdmin,
  selectedRestId,
  restaurantName,
}: {
  isSuperAdmin: boolean;
  selectedRestId: string;
  restaurantName: string;
}) => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const restId = restaurantId || selectedRestId;

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayEnd = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const [startDate, setStartDate] = useState(todayStart);
  const [endDate, setEndDate] = useState(todayEnd);
  const [tableFilter, setTableFilter] = useState(ALL_TABLES);
  const [groupFilter, setGroupFilter] = useState(ALL_GROUPS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<KotLogEntry[]>([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate(restId ? `/admin/${restId}/orders` : '/admin', { replace: true });
    }
  }, [isSuperAdmin, navigate, restId]);

  const loadLogs = useCallback(async () => {
    if (!restId || !isSuperAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const startMs = moment(startDate).startOf('day').valueOf();
      const endMs = moment(endDate).endOf('day').valueOf();
      const data = await getKotLogsForDateRange(restId, startMs, endMs);
      setEntries(data);
      setTableFilter(ALL_TABLES);
      setGroupFilter(ALL_GROUPS);
    } catch (err) {
      console.error(err);
      setEntries([]);
      setError(
        (err as Error)?.message ||
          'Failed to load KOT logs. If this is a new query, create the Firestore composite index when prompted.'
      );
    } finally {
      setLoading(false);
    }
  }, [restId, startDate, endDate, isSuperAdmin]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const tableOptions = useMemo(() => {
    const tables = Array.from(
      new Set(entries.map((e) => e.tableNumber).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    return [ALL_TABLES, ...tables.map((t) => ({ label: t, value: t }))];
  }, [entries]);

  const groupOptions = useMemo(() => {
    const scoped = tableFilter.value
      ? entries.filter((e) => e.tableNumber === tableFilter.value)
      : entries;
    const groups = Array.from(
      new Set(scoped.map((e) => e.kotSessionId).filter(Boolean) as string[])
    ).sort();
    return [ALL_GROUPS, ...groups.map((g) => ({ label: g, value: g }))];
  }, [entries, tableFilter]);

  const filteredEntries = useMemo(() => {
    let rows = entries;
    if (tableFilter.value) {
      rows = rows.filter((e) => e.tableNumber === tableFilter.value);
    }
    if (groupFilter.value) {
      rows = rows.filter((e) => e.kotSessionId === groupFilter.value);
    }
    return sortKotLogsByTableThenDate(rows);
  }, [entries, tableFilter, groupFilter]);

  const summary = useMemo(() => buildKotDailySummary(filteredEntries), [filteredEntries]);

  const rowData = useMemo(
    () => filteredEntries.map(toKotLogGridRow),
    [filteredEntries]
  );

  const exportFilename = useMemo(
    () => getKotExportFilename(restaurantName || restId, startDate, endDate),
    [restaurantName, restId, startDate, endDate]
  );

  const handleDownloadCsv = useCallback(() => {
    const rows = buildKotExportRows(rowData);
    if (!rows.length) return;
    const csvExporter = new ExportToCsv({
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'KOT Logging Report',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      filename: exportFilename,
    });
    csvExporter.generateCsv(rows);
  }, [rowData, exportFilename]);

  const handleDownloadExcel = useCallback(() => {
    downloadExcelFromRows(buildKotExportRows(rowData), exportFilename);
  }, [rowData, exportFilename]);

  const pinnedTopRowData = useMemo(
    () => [
      {
        id: 'Total:',
        isTotalRow: true,
        tableNumber: 'Total',
        printedAtMs: 0,
        kotSessionId: '',
        printType: '',
        serviceLabel: '',
        totalQty: summary.totalQty,
        itemCount: summary.itemLineCount,
        totalItemsCost: summary.totalItemsCost,
        printedByLabel: '',
        printerChannel: '',
        source: '',
        itemsSummary: '',
        items: [],
        restaurantId: '',
        serviceType: 'dine-in',
        uoId: null,
        orderId: null,
        printedBy: { uid: '', phone: '', name: '' },
        status: 'printed',
        localDate: '',
        clientRequestId: '',
      },
    ],
    [summary.totalQty, summary.itemLineCount, summary.totalItemsCost]
  );

  if (!isSuperAdmin) {
    return null;
  }

  const canDownload = !loading && rowData.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3, minHeight: 'calc(100vh - 160px)' }} className="mobReportWrap">
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          KOT Logging Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {restaurantName || restId} · confirmed kitchen prints only
        </Typography>
      </Box>

      <div className="filterRow filterRow--secondary reportFilters" style={{ marginBottom: 20 }}>
        <div className="filterBlock filterBlock--inline filterBlock--dates">
          <div className="filterLabel">Date Range (From - To)</div>
          <div className="dateRange">
            <div className="dateField">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => date && setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="d MMM yyyy"
                placeholderText="Start date"
              />
            </div>
            <div className="dateField">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  if (date) {
                    const end = new Date(date);
                    end.setHours(23, 59, 59, 999);
                    setEndDate(end);
                  }
                }}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="d MMM yyyy"
                placeholderText="End date"
              />
            </div>
          </div>
        </div>

        <div className="filterBlock filterBlock--inline filterBlock--service">
          <div className="filterLabel">Table Name</div>
          <ReactSelect
            className="filterTypes"
            value={tableFilter}
            options={tableOptions}
            onChange={(opt: any) => {
              setTableFilter(opt || ALL_TABLES);
              setGroupFilter(ALL_GROUPS);
            }}
          />
        </div>

        <div className="filterBlock filterBlock--inline filterBlock--payment">
          <div className="filterLabel">Group ID</div>
          <ReactSelect
            className="filterTypes"
            value={groupFilter}
            options={groupOptions}
            onChange={(opt: any) => setGroupFilter(opt || ALL_GROUPS)}
          />
        </div>
      </div>

      <div className="filterRow filterRow--primary actionButtons" style={{ marginBottom: 16 }}>
        <div className="filterBlock">
          <div className="totalText">Total KOTs: [ {summary.kotCount} ]</div>
        </div>
        <div className="filterBlock filterBlock--right">
          <ButtonGroup variant="outlined" color="primary" className="actionButtons__group">
            <Button
              className="btnReport"
              disabled={!canDownload}
              onClick={handleDownloadCsv}
            >
              D-CSV
            </Button>
            <Button
              className="btnReport"
              disabled={!canDownload}
              onClick={handleDownloadExcel}
            >
              D-XLS
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <SummaryChip label="KOTs printed" value={summary.kotCount} />
        <SummaryChip label="Total qty" value={summary.totalQty} />
        <SummaryChip
          label="Items cost"
          value={`₹${Number(summary.totalItemsCost || 0).toFixed(2)}`}
        />
        <SummaryChip label="Line items" value={summary.itemLineCount} />
        {Object.entries(summary.byPrintType).map(([key, val]) => (
          <SummaryChip key={key} label={key} value={val} />
        ))}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="secondary" />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && filteredEntries.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <Typography color="text.secondary">
            No confirmed KOT prints for{' '}
            {moment(startDate).format('DD MMM YYYY')}
            {moment(startDate).format('YYYY-MM-DD') !== moment(endDate).format('YYYY-MM-DD')
              ? ` – ${moment(endDate).format('DD MMM YYYY')}`
              : ''}
            {tableFilter.value ? ` · table ${tableFilter.value}` : ''}
            {groupFilter.value ? ` · group ${groupFilter.value}` : ''}.
          </Typography>
        </Paper>
      )}

      {!loading && filteredEntries.length > 0 && (
        <AgGrid
          rowData={rowData}
          colDefs={kotLoggingColDefs}
          pinnedTopRowData={pinnedTopRowData}
          defaultColDef={{
            resizable: true,
            sortable: false,
          }}
          initialState={{
            sort: {
              sortModel: [
                { colId: 'printedAtMs', sort: 'desc' },
              ],
            },
          }}
          tooltipShowDelay={400}
          suppressRowClickSelection
          suppressCellFocus
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  const { user, common } = state;
  return {
    isSuperAdmin: !!(user?.user?.isSuperAdmin),
    selectedRestId: common?.selectedRestaurant?.docId || '',
    restaurantName: common?.selectedRestaurant?.name || '',
  };
};

export default connect(mapStateToProps)(KotLoggingReport);
